import React, { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { TrendingUp, Radio, Target, CalendarCheck, Megaphone, Layers } from "lucide-react";

const AUDIENCES = [
  {
    id: "artists",
    kicker: "01 / Artists",
    title: ["FOR ARTISTS,", "BANDS &", "MUSICIANS"],
    goal: "More fans. More followers. More streams.",
    description:
      "You make the music. We build the system that gets it heard — content, campaigns and strategy that compound release after release.",
    bullets: [
      { icon: Layers, text: "Content systems — reels, live clips, release assets" },
      { icon: CalendarCheck, text: "Release & campaign cadence that compounds" },
      { icon: TrendingUp, text: "Growth strategy, executed with you" },
    ],
    cta: "Start Artist Consult",
    ctaTestId: "artists-section-cta-button",
    hint: "artist",
    tilt: 1,
  },
  {
    id: "businesses",
    kicker: "02 / Music Businesses",
    title: ["FOR MUSIC", "BUSINESSES"],
    goal: "More bookings. More conversions. More sales.",
    description:
      "Studios · Labels · Music Schools · Event Companies · Audio Brands · Venues. Marketing built by people who actually know the music industry.",
    bullets: [
      { icon: Target, text: "Lead-gen funnels & landing systems" },
      { icon: Megaphone, text: "Content + ads + retargeting that convert" },
      { icon: Radio, text: "Conversion-focused creative from inside the scene" },
    ],
    cta: "Start Business Consult",
    ctaTestId: "businesses-section-cta-button",
    hint: "business",
    tilt: -1,
  },
];

const AudienceCard = ({ audience, onCTA }) => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const rotateY = useTransform(scrollYProgress, [0, 0.5, 1], [audience.tilt * 7, 0, audience.tilt * -5]);
  const y = useTransform(scrollYProgress, [0, 0.5, 1], [48, 0, -32]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.965, 1, 0.985]);

  return (
    <div ref={ref} id={audience.id} className="[perspective:1400px]" data-testid={`${audience.id}-section`}>
      <motion.div
        style={reduce ? {} : { rotateY, y, scale }}
        className="transform-gpu will-change-transform rounded-3xl border border-white/10 bg-[color:var(--hs-bg-1)] shadow-[var(--shadow-elev-2)] overflow-hidden"
      >
        <div className="grid lg:grid-cols-2">
          <div className="p-7 sm:p-10 lg:p-14">
            <p className="font-mono-ui text-[11px] tracking-[0.35em] uppercase text-[color:var(--hs-lime)]">
              {audience.kicker}
            </p>
            <h2 className="mt-4 font-display font-display-rough uppercase leading-[0.95] text-4xl sm:text-5xl lg:text-6xl text-white">
              {audience.title.map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </h2>
            <p className="mt-5 font-display uppercase text-lg sm:text-xl text-[color:var(--hs-lime)] tracking-wide">
              {audience.goal}
            </p>
            <p className="mt-4 text-sm sm:text-base text-[color:var(--hs-ink-dim)] leading-relaxed max-w-md">
              {audience.description}
            </p>
            <Button
              onClick={() => onCTA(audience.hint)}
              data-testid={audience.ctaTestId}
              className="btn-lift glow-lime mt-8 bg-[color:var(--hs-lime)] text-[color:var(--hs-bg-0)] hover:bg-[color:var(--hs-lime-2)] font-semibold rounded-xl h-12 px-7"
            >
              {audience.cta}
            </Button>
          </div>
          <div className="relative border-t lg:border-t-0 lg:border-l border-white/10 bg-[color:var(--hs-bg-2)] grid-lines">
            <div className="p-7 sm:p-10 lg:p-14 flex flex-col justify-center h-full gap-4">
              {audience.bullets.map((bullet, i) => {
                const Icon = bullet.icon;
                return (
                  <motion.div
                    key={bullet.text}
                    initial={{ opacity: 0, x: 28 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true, amount: 0.5 }}
                    transition={{ duration: 0.55, delay: i * 0.1, ease: [0.2, 0.8, 0.2, 1] }}
                    className="flex items-start gap-4 rounded-2xl border border-white/10 bg-black/30 backdrop-blur-sm p-4 sm:p-5"
                  >
                    <span className="mt-0.5 h-9 w-9 shrink-0 rounded-xl bg-[color:rgba(202,255,26,0.12)] border border-[color:rgba(202,255,26,0.3)] flex items-center justify-center">
                      <Icon className="h-4.5 w-4.5 text-[color:var(--hs-lime)]" size={18} />
                    </span>
                    <span className="text-sm sm:text-base text-white/90 leading-snug">{bullet.text}</span>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export const AudienceSplit = ({ onCTA }) => {
  return (
    <section className="relative py-16 sm:py-20 lg:py-28" data-testid="audience-split-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 sm:mb-16"
        >
          <p className="font-mono-ui text-[11px] tracking-[0.35em] uppercase text-[color:var(--hs-ink-dim)]">
            One stop shop for music growth
          </p>
          <h2 className="mt-3 font-display font-display-rough uppercase text-3xl sm:text-4xl lg:text-5xl text-white">
            Who we <span className="text-[color:var(--hs-lime)]">grow</span>
          </h2>
        </motion.div>
        <div className="space-y-10 sm:space-y-14">
          {AUDIENCES.map((audience) => (
            <AudienceCard key={audience.id} audience={audience} onCTA={onCTA} />
          ))}
        </div>
      </div>
    </section>
  );
};
